import { LocalStorageKey, SessionStorageKey } from '@/lib/storageConstants'
import { CONFIG } from '@/config/config'
import { cn } from '@/lib/utils'
import { toast } from '@/components/ui/sonner'
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect'
import { AdminClient, OnboardingStep } from '@storefronts/common-types'
import { useRouter } from 'next/router'
import { usePostHog } from 'posthog-js/react'
import { useContext, useEffect, useState } from 'react'
import { themes } from '../../styles/Theme'
import { isInternalUser } from '@storefronts/common-types'
import { R } from '../modules/api/actions'
import { AuthContext } from '../modules/auth/authContext'
import { processClientHashFromUrl } from '../modules/auth/processClientHashFromUrl'
import { unprotectedRoutes } from '../modules/auth/unprotectedRoutes'
import { consumeReturnPath, storeReturnPath } from '../modules/auth/returnPath'
import { BrandSetupContextProvider } from './brand-setup/BrandSetupContext'
import { BrandedLoadingScreen } from './shared/BrandedLoadingScreen'
import { AppShellProvider } from './shared/AppShellContext'
import { SideNavigation } from './navigation/SideNavigation'
import { SidebarProvider } from './ui/sidebar'
import { TooltipProvider } from './ui/tooltip'
import { ShopifySyncBanner, useShopifySyncBanner } from './ShopifySyncBanner'

export const AuthLayout: React.FunctionComponent<{
    children: React.ReactNode
}> = ({ children }) => {
    const router = useRouter()
    const authContext = useContext(AuthContext)
    const [isAuthSetup, setIsAuthSetup] = useState(false)
    const [showLoading, setShowLoading] = useState(true)
    const [canMountAppShell, setCanMountAppShell] = useState(false)
    const posthog = usePostHog()
    const syncBanner = useShopifySyncBanner()

    useIsomorphicLayoutEffect(() => {
        const clientHash = window.localStorage.getItem(
            LocalStorageKey.CLIENT_HASH,
        )
        if (clientHash) {
            R.setClient(clientHash)
            setCanMountAppShell(true)
        }
    }, [])

    // TODO: how to share this duplicate code with OnboardingLayout
    useEffect(() => {
        if (!router.isReady || router.pathname === '/login') {
            return
        }
        // make profile request
        R.getProfile()
            // eslint-disable-next-line complexity
            .then(async (resp) => {
                if (resp.isAuthenticated) {
                    let redirectingToOnboarding = false
                    // TODO: is this necessary? R.getProfile already sets the client
                    if (resp.user.client) {
                        R.setClient(resp.user.client.clientHash)
                        setCanMountAppShell(true)
                    }
                    authContext.setLoggedIn(true)
                    authContext.setUser(resp.user)
                    if (isInternalUser(resp.user.username)) {
                        authContext.setIsSuperUser(true)
                    }

                    // Check if user has any clients
                    if (!resp.user.client) {
                        redirectingToOnboarding = true
                        router.push('/onboarding')
                    }

                    const clientInLocalStorage = window.localStorage.getItem(
                        LocalStorageKey.CLIENT_HASH,
                    )

                    let client: AdminClient
                    if (
                        resp.user.client &&
                        (!clientInLocalStorage ||
                            clientInLocalStorage ===
                                resp.user.client.clientHash)
                    ) {
                        // If no stored client or stored client is the user's primary client
                        client = resp.user.client
                    }

                    if (resp.user.client) {
                        // If persisted client is one of user's other clients
                        const allClientsResp = await R.getAvailableClients()
                        const allClients = allClientsResp.clients

                        const filteredClient = allClients.find(
                            (c) => c.clientHash === clientInLocalStorage,
                        )

                        if (filteredClient) {
                            // If persisted client is one of user's other clients
                            client = filteredClient
                        } else {
                            //Fallback
                            client = resp.user.client
                        }

                        // Shopify app install redirect: switch to client from URL if user has access
                        const { client: clientAfterUrl, accessDenied } =
                            processClientHashFromUrl({
                                query: router.query,
                                pathname: router.pathname,
                                replace: router.replace,
                                allClients,
                                currentClient: client,
                            })
                        client = clientAfterUrl
                        if (accessDenied) {
                            toast.error('No access', {
                                description:
                                    "You don't have access to this client.",
                            })
                        }

                        window.localStorage.setItem(
                            LocalStorageKey.CLIENT_HASH,
                            client?.clientHash,
                        )
                        authContext.setClient(client)
                        R.setClient(client?.clientHash)

                        // Identify posthog
                        posthog.identify(resp.user.username, {
                            username: resp.user.username,
                            client: client?.name,
                        })

                        // Check if we need to redirect to onboarding
                        if (
                            !unprotectedRoutes.includes(router.pathname) &&
                            client?.onboardingStep
                        ) {
                            // Check if this is a SALES client who has completed frontend META_SETUP
                            const salesClientMetaSetupCompleted =
                                window.sessionStorage.getItem(
                                    SessionStorageKey.SALES_CLIENT_META_SETUP_COMPLETED,
                                )

                            // Don't redirect to onboarding if SALES client has completed frontend META_SETUP
                            // This is a temporary bypass that expires when the browser tab closes
                            // Use AuthContext.client for the bypass check to ensure client consistency
                            const isSalesClientWithCompletedMetaSetup =
                                authContext.client?.leadSource === 'SALES' &&
                                salesClientMetaSetupCompleted ===
                                    authContext.client?.clientHash

                            const disabledSteps = CONFIG.SELF_SERVE_DISABLED
                                ? [
                                      OnboardingStep.ACTIVATE_TRIAL,
                                      OnboardingStep.BOOK_CALL,
                                      OnboardingStep.CHECKOUT_COMPLETED,
                                      OnboardingStep.RESTART_SUBSCRIPTION,
                                  ]
                                : []
                            const isDisabledStep = disabledSteps.includes(
                                client.onboardingStep as OnboardingStep,
                            )

                            if (
                                !isSalesClientWithCompletedMetaSetup &&
                                !isDisabledStep
                            ) {
                                redirectingToOnboarding = true
                                router.push(
                                    `/onboarding?step=${client.onboardingStep}`,
                                )
                            }
                        }
                    }

                    if (!redirectingToOnboarding) {
                        const returnTo = consumeReturnPath()
                        if (returnTo && returnTo !== router.asPath) {
                            router
                                .push(returnTo)
                                .catch(() => undefined)
                                .finally(() => setIsAuthSetup(true))
                            return
                        }
                    }

                    setIsAuthSetup(true)
                    return
                }

                if (
                    !unprotectedRoutes.includes(router.pathname) &&
                    (authContext.isLoggedIn === null ||
                        authContext.user === null)
                ) {
                    storeReturnPath(router.asPath)
                    router.push('/login')
                }
            })
            .catch((err) => {
                console.error(err)
                storeReturnPath(router.asPath)
                router.push('/login')
                toast.warning('Unable to log you in.', {
                    description: '',
                })
            })
    }, [router.isReady]) // eslint-disable-line react-hooks/exhaustive-deps

    const isAuthReady = !!authContext.isLoggedIn && isAuthSetup
    const shouldMountApp = canMountAppShell || isAuthSetup

    return (
        <div
            className={cn(
                'relative flex min-h-screen w-screen flex-col bg-muted',
                syncBanner && 'pt-12',
            )}
            style={{ fontFamily: themes.fonts.sans }}
        >
            {shouldMountApp ? (
                <BrandSetupContextProvider>
                    <AppShellProvider isShellVisible={!showLoading}>
                        <div
                            className={cn(showLoading && 'invisible')}
                            aria-hidden={showLoading}
                        >
                            <ShopifySyncBanner />
                            <SidebarProvider
                                hoverExpand
                                className="relative flex min-h-0 w-full"
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <div
                                    className={cn(
                                        'pointer-events-auto z-40 w-full md:z-20 md:w-auto',
                                        'fixed bottom-0 left-0 md:static',
                                        syncBanner
                                            ? 'top-12 md:top-auto'
                                            : 'top-0 md:top-auto',
                                    )}
                                >
                                    <SideNavigation
                                        provideDesktopSidebar={false}
                                        syncBannerVisible={!!syncBanner}
                                    />
                                </div>
                                <div
                                    className={cn(
                                        'flex min-w-0 w-full flex-[5] flex-col bg-card p-4 md:overflow-auto',
                                        syncBanner
                                            ? 'md:h-[calc(100vh-3rem)]'
                                            : 'md:h-screen',
                                        'h-auto overflow-visible rounded-none border-0 shadow-none',
                                    )}
                                >
                                    <TooltipProvider>
                                        {children}
                                    </TooltipProvider>
                                </div>
                            </SidebarProvider>
                        </div>
                    </AppShellProvider>
                </BrandSetupContextProvider>
            ) : null}
            {showLoading && (
                <BrandedLoadingScreen
                    overlay
                    isReady={isAuthReady}
                    onFadeComplete={() => setShowLoading(false)}
                />
            )}
        </div>
    )
}
